import { Suspense, ErrorInfo, lazy } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Outlet } from 'react-router-dom';

import { Spinner } from '@/components/ui/spinner';
import { OnboardingRouteSync } from '@/features/onboarding';
import { reportException, reportErrorWithMessage } from '@/lib/sentry';
import { useTokenErrorHandler } from '@/providers/useTokenErrorHandler';
import { log, error as printError } from '@/utils/logger';

// lazy so the project feature stays out of the entry chunk - it's the same
// `@/features/project` chunk the project routes load anyway
const PendingNavigationGuard = lazy(() =>
  import('@/features/project').then((m) => ({
    default: m.PendingNavigationGuard,
  })),
);

/**
 * Call resetErrorBoundary() to reset the error boundary and retry the render.
 *
 * @returns
 */
const fallbackRender = ({ error }: FallbackProps) => {
  return (
    <div role="alert" className="bg-white">
      <p>Something went wrong:</p>
      <pre className="text-red-600 border border-black p-2 bg-gray-100">
        {error.message}
      </pre>
    </div>
  );
};

const logError = (error: Error, info: ErrorInfo) => {
  // Do something with the error, e.g. log to an external API
  reportException(error);

  log('## error message sent to sentry! ##');
  printError(error.message);

  if (!info.componentStack) return;
  reportErrorWithMessage(info.componentStack);
  log(info.componentStack);
};

/**
 * Page lazy loader & global error processer
 * @date 2024/10/30
 * @returns
 */
export const PageSafeWrapper = () => {
  /**
   * Handle global token error from http client!
   * Enable this @2024/02/12
   */
  useTokenErrorHandler();

  /**
   * identify logged in user when location changed
   */
  // useLogRocketIdentify();

  return (
    <>
      {/* Syncs pathname into onboarding store — only active inside protected routes */}
      <OnboardingRouteSync />
      {/* FIXME: observe back navigation at project module, rather than `FileDetailsView`
          ony in this way, the `popstate` can work perfectly!
          @2023/05/05 */}
      <Suspense fallback={null}>
        <PendingNavigationGuard />
      </Suspense>
      <Suspense
        fallback={
          <div className="flex size-full items-center justify-center">
            <Spinner size="xl" />
          </div>
        }
      >
        {/* second level error boundary at protected routes */}
        {/* render child route elements */}
        <ErrorBoundary
          key={location.pathname}
          fallbackRender={fallbackRender}
          onError={logError}
        >
          <Outlet />
        </ErrorBoundary>
      </Suspense>
    </>
  );
};
